const DEFAULT_INTERVAL_MS = 45_000;

const SECTION_RESOURCE_DEPENDENCIES = {
  dashboard: new Set(['dashboard', 'offers', 'orders', 'invoices', 'materials', 'warehouse', 'customers']),
  nabidky: new Set(['offers', 'customers']),
  objednavky: new Set(['orders', 'offers', 'invoices', 'customers']),
  vyroba: new Set(['orders', 'customers']),
  faktury: new Set(['invoices', 'orders', 'customers']),
  soubory: new Set(['files', 'customers']),
};

function getSectionRefresher(sectionId) {
  const refreshers = {
    dashboard: () => window.dashboardManager?.refresh?.(),
    nabidky: () => window.offersListManager?.refresh?.(),
    objednavky: () => window.ordersListManager?.refresh?.(),
    vyroba: () => window.vyrobaManager?.refresh?.(),
    faktury: () => window.invoicesListManager?.refresh?.(),
    soubory: () => window.filesManager?.refresh?.()
  };
  return refreshers[sectionId] || null;
}

class AutoRefreshManager {
  constructor({ intervalMs = DEFAULT_INTERVAL_MS } = {}) {
    this.intervalMs = intervalMs;
    this.currentSection = null;
    this.lastRefreshAt = new Map();
    this.inFlight = new Set();
    this.timer = null;
  }

  start() {
    if (this.timer) return;
    this.currentSection = window.sectionManager?.currentSection || null;

    this.timer = window.setInterval(() => {
      this.refreshCurrentSection('interval');
    }, this.intervalMs);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshCurrentSection('tab-visible', { force: true });
      }
    });

    window.addEventListener('focus', () => {
      this.refreshCurrentSection('window-focus', { force: true });
    });
  }

  setCurrentSection(sectionId) {
    this.currentSection = sectionId || null;
  }

  shouldRefreshSectionForPush(sectionId, eventPayload) {
    if (!sectionId) return false;

    const resource = eventPayload?.resource;
    if (!resource || typeof resource !== 'string') {
      // Bez resource metadata fallback na refresh, aby se změna neztratila.
      return true;
    }

    const dependencies = SECTION_RESOURCE_DEPENDENCIES[sectionId];
    if (!dependencies) return false;
    return dependencies.has(resource);
  }

  handlePushEvent(eventPayload) {
    if (!this.currentSection) return;
    if (!this.shouldRefreshSectionForPush(this.currentSection, eventPayload)) return;
    this.refreshCurrentSection('push', { force: true });
  }

  refreshCurrentSection(reason, { force = false } = {}) {
    if (!window.__appReady) return;
    if (document.visibilityState !== 'visible') return;
    if (!this.currentSection) return;

    const refresher = getSectionRefresher(this.currentSection);
    if (!refresher) return;
    if (this.inFlight.has(this.currentSection)) return;

    const now = Date.now();
    const last = this.lastRefreshAt.get(this.currentSection) || 0;
    if (!force && now - last < this.intervalMs) return;

    this.inFlight.add(this.currentSection);
    this.lastRefreshAt.set(this.currentSection, now);

    Promise.resolve(refresher(reason))
      .catch((err) => {
        // Auto-refresh běží na pozadí – chyba nesmí rušit UI.
        console.warn('[auto-refresh] Refresh selhal:', this.currentSection, err);
      })
      .finally(() => {
        this.inFlight.delete(this.currentSection);
      });
  }
}

export function initAutoRefresh(options = {}) {
  const manager = new AutoRefreshManager(options);
  manager.start();
  return manager;
}
